import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import appTables from '../../../assets/data/appTables.json';
import systemTables from '../../../assets/data/systemTables.json';
import api from '../../../services/api';
import dbStorageService from '../../../services/dbStorageService';
import TableSearchInput from './TableSearchInput';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';
import { ActionButton } from '../forms';

const TableList = ({ tableType = 'all', breadcrumbs }) => {
  const { semantic } = useColors();
  const { replace } = useTextReplacement();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [localTables, setLocalTables] = useState(null);
  const [searchQueries, setSearchQueries] = useState({
    local: '',
    system: '',
    app: ''
  });

  useEffect(() => {
    if (tableType === 'local' || tableType === 'all') {
      const loadLocalTables = async () => {
        try {
          const tables = await dbStorageService.getLocalTables();
          setLocalTables(tables);
        } catch (error) {
          console.error('Error loading local tables:', error);
          setLocalTables({ tablesSummary: {} }); // Set empty state on error
        }
      };
      loadLocalTables();
    }
  }, [tableType]);

  const handleSearchChange = (type, value) => {
    setSearchQueries(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const filterTables = (tables, type) => {
    const query = searchQueries[type].toLowerCase();
    return Object.entries(tables.tablesSummary).filter(([_, details]) => {
      return (
        details.displayName.toLowerCase().includes(query) ||
        details.description.toLowerCase().includes(query)
      );
    }).sort(([nameA], [nameB]) => nameA.localeCompare(nameB));
  };

  const navigateToTableEditor = (prefix, tableName) => {
    navigate(`/table-editor/${prefix}/${tableName}`, {
      state: { breadcrumbs }
    });
  };

  const exportTableDataAsJSON = async (prefix, tableName) => {
    setLoading(true);
    try {
      let tableData;
      
      if (prefix === 'local') {
        // For local tables, get data from IndexedDB
        tableData = await dbStorageService.getAllRecords(tableName);
      } else {
        // For system and app tables, get data from API
        const queryStringGetAllRecords = "?page=-1";
        const endpoint = prefix === 'system' ? `/api/system/${tableName}` : `/api/tbl/${tableName}`;
        const response = await api.get(endpoint + queryStringGetAllRecords);
        tableData = response.data.rows;
      }

      const dataStr = JSON.stringify(tableData, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tableName}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting table data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderTableList = (tables, prefix) => {
    const filteredTables = filterTables(tables, prefix);

    return filteredTables.map(([tableName, details]) => (
      <div key={tableName} className={`${semantic.contentCardBg} ${semantic.contentCardBorder} p-4 mb-4 rounded-lg shadow`}>
        <div className="flex justify-between items-center">
          <div>
            <h3 className={`text-xl font-semibold px-1 text-left ${semantic.contentCardHeading}`}>
              {details.displayName}
            </h3>
            <p className={`text-left ${semantic.contentCardText}`}>
              {details.description}
            </p>
            {details.recordCount !== undefined && (
              <p className={`text-left text-sm ${semantic.formText}`}>
                {replace('Records')}: {details.recordCount}
              </p>
            )}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => navigateToTableEditor(prefix, tableName)}
              className={`px-4 py-2 ${semantic.buttonSecondary} rounded transition-colors`}
              aria-label={replace(`Edit ${details.displayName} table`)}
            >
              {replace('Edit Table')}
            </button>
            <button
              onClick={() => exportTableDataAsJSON(prefix, tableName)}
              disabled={loading}
              className={`px-4 py-2 ${semantic.buttonSecondary} rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              aria-label={replace(`Export ${details.displayName} table as JSON`)}
            >
              {loading ? replace('Exporting...') : replace('Export as JSON')}
            </button>
          </div>
        </div>
      </div>
    ));
  };

  return (
    <div>
      {(tableType === 'local' || tableType === 'all') && localTables && (
        <div className="mb-8">
          <TableSearchInput
            type="local"
            value={searchQueries.local}
            onChange={handleSearchChange}
          />
          {renderTableList(localTables, 'local')}
        </div>
      )}

      {(tableType === 'system' || tableType === 'all') && (
        <div className="mb-8">
          <TableSearchInput
            type="system"
            value={searchQueries.system}
            onChange={handleSearchChange}
          />
          {renderTableList(systemTables, 'system')}
        </div>
      )}

      {(tableType === 'app' || tableType === 'all') && (
        <div>
          <TableSearchInput
            type="app"
            value={searchQueries.app}
            onChange={handleSearchChange}
          />
          {renderTableList(appTables, 'app')}
        </div>
      )}
    </div>
  );
};

TableList.propTypes = {
  tableType: PropTypes.oneOf(['all', 'system', 'app', 'local']),
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired
    })
  )
};

export default TableList;

